import { useEffect, useMemo, useRef } from 'react';
import DefeatDie from './DefeatDie';
import CoreboundIcon from './CoreboundIcon';
import { getDie } from './dice';
import { playSound } from '../../utils/utils';
import './DefeatSequence.css';

const SEQUENCE_DURATION = 3800;

const DefeatSequence = ({
  bossRound,
  floor,
  dieId,
  faceValues,
  rollValue,
  onComplete,
  reduceMotion,
}) => {
  const dialogRef = useRef(null);
  const skipRef = useRef(null);
  const completedRef = useRef(false);
  const fracturePlayedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const faces = useMemo(
    () =>
      faceValues?.length === 6
        ? faceValues
        : getDie(dieId).faces || [rollValue ?? '?', '?', '?', '?', '?', '?'],
    [dieId, faceValues, rollValue]
  );

  useEffect(() => {
    const playFracture = () => {
      if (completedRef.current || fracturePlayedRef.current) return;
      fracturePlayedRef.current = true;
      playSound('/sounds/stop-time-explosion.mp3', { preventOverlap: true });
    };
    if (reduceMotion) {
      playFracture();
      return undefined;
    }
    // Match the 1.05s cut in defeatDieScene and the CSS fallback split.
    const timer = window.setTimeout(playFracture, 1050);
    return () => window.clearTimeout(timer);
  }, [reduceMotion]);

  useEffect(() => {
    dialogRef.current?.focus({ preventScroll: true });
    const timer = window.setTimeout(() => {
      if (completedRef.current) return;
      completedRef.current = true;
      onCompleteRef.current();
    }, SEQUENCE_DURATION);

    return () => window.clearTimeout(timer);
  }, []);

  const finishSequence = () => {
    if (completedRef.current) return;
    completedRef.current = true;
    onCompleteRef.current();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      finishSequence();
      return;
    }

    if (event.key === 'Tab') {
      event.preventDefault();
      if (event.shiftKey && document.activeElement === skipRef.current) {
        dialogRef.current?.focus();
      } else {
        skipRef.current?.focus();
      }
    }
  };

  const failureDetail = bossRound
    ? `The Core silenced your signal in round ${bossRound}.`
    : `Sector ${floor} overwhelmed your signal.`;

  return (
    <section
      ref={dialogRef}
      className={`coreboundDefeatCinematic ${
        reduceMotion ? 'coreboundDefeatCinematic-static' : ''
      }`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="coreboundDefeatCinematicTitle"
      aria-describedby="coreboundDefeatCinematicDetail"
      tabIndex={-1}
      onKeyDown={handleKeyDown}
    >
      <div className="coreboundDefeatCinematicScene" aria-hidden="true">
        <div className="coreboundDefeatCinematicGrid" />
        <div className="coreboundDefeatCinematicShockwave" />
        <div className="coreboundDefeatCinematicCore">
          <i />
          <i />
          <i />
          <b />
        </div>
        <DefeatDie
          dieId={dieId}
          faceValues={faces}
          reduceMotion={reduceMotion}
        />
      </div>

      <div className="coreboundDefeatCinematicCopy">
        <span className="coreboundDefeatCinematicKicker">
          COREBOUND // RUN TERMINATED
        </span>
        <h1 id="coreboundDefeatCinematicTitle">SIGNAL SEVERED</h1>
        <p id="coreboundDefeatCinematicDetail">{failureDetail}</p>
      </div>

      <button
        ref={skipRef}
        type="button"
        className="coreboundDefeatCinematicSkip"
        onClick={finishSequence}
      >
              Skip sequence <span aria-hidden="true"><CoreboundIcon name="relay" /></span>
      </button>
    </section>
  );
};

export default DefeatSequence;
